import { Component, h, Host, State, Prop, Watch, Element } from '@stencil/core';
import { DATA_VISUALIZATION_HUE, DATA_VISUALIZATION_HUE_VARIANT } from '../../constants';
import { getCssColorCode } from '../../utils/color-helper';

@Component({
  tag: 'uui-variable-percent-bar',
  styleUrl: 'uui-variable-percent-bar.scss',
  shadow: true
})
export class UuiVariablePercentBar {
  @Element() element: HTMLElement;

  /** 
    customize label to display 
  */
  @Prop() totalLabel?: string;
  /** 
    override default tooltip Title
  */
  @Prop() tooltipTitle?: string = '';
  /** 
    overide tooltip row values
  */
  @Prop() tooltipLabels?: string[];
  /** 
    option to override the default colours
  */
  @Prop() colors?: string[];
  /** 
    segment values
  */
  @Prop() segmentTotals: number[] = [];
  /** 
    ranges corresponding to segments
  */
  @Prop() segmentRanges: string[] = ['Not Scored', '0-25', '26-50', '51-75', '76-100'];
  /** 
    configure whether to show segement to total difference in percentage
  */
  @Prop() isShowSegmentTotalPercentage: boolean = true;
  /** 
    enable tooltip display
  */
  @Prop() isEnableTooltip: boolean = false;

  @State() segments: Array<{ color: string, quantity: number, percentOfTotal: number }>;
  @State() displayTooltip: boolean = false;

  public defaultColors = [
    DATA_VISUALIZATION_HUE_VARIANT.GRAY,
    DATA_VISUALIZATION_HUE_VARIANT.RED,
    DATA_VISUALIZATION_HUE_VARIANT.ORANGE,
    DATA_VISUALIZATION_HUE_VARIANT.YELLOW,
    DATA_VISUALIZATION_HUE_VARIANT.GREEN,
  ];
  public aggregatedTotal: number = 0;
  hasInvalidDataSlot: boolean;
  toolTipElement: HTMLElement;

  @Watch('segmentTotals')
  onSegmentTotalsChanged(): void {
    this.aggregatedTotal = (this.segmentTotals as number[]).reduce((acc, cur) => acc + cur, 0);
    this.computeSegments();
  }

  componentWillLoad() {
    this.hasInvalidDataSlot = !!this.element.querySelector(
      '[slot="invalid-data"]'
    );

    this.aggregatedTotal = (this.segmentTotals as number[]).reduce((acc, cur) => acc + cur, 0);
    this.computeSegments();
  }

  computeSegments() {
    this.segments = [];
    (this.segmentTotals as number[]).forEach((quantity, index) => {
      this.segments.push({
        color: this.getColor(index),
        quantity,
        percentOfTotal: Math.round(((quantity / this.aggregatedTotal) * 100) * 10) / 10,
      });
    });
  }

  getColor(index: number): string {
    if (this.colors && this.colors.length > index) {
      return getCssColorCode(this.colors[index]);
    }

    return DATA_VISUALIZATION_HUE[this.defaultColors[index]];
  };

  //#region - Tooltip
  toogleTooltip(isShow: boolean): void {
    this.toolTipElement.style.opacity = isShow ? '1' : '0';
  }

  updateTooltipPosition(x: number, y: number): void {
    const tooltipHeight = this.toolTipElement?.getBoundingClientRect()?.height + 15;
    const tooltipWidth = this.toolTipElement?.getBoundingClientRect()?.width;

    this.toolTipElement.style.left = `${x - (tooltipWidth / 2)}px`;
    this.toolTipElement.style.top = `${y - tooltipHeight}px`;
  }

  getChartTooltip(): JSX.Element {
    return (
      <uui-percent-bar-tooltip 
        tooltipTitle={this.tooltipTitle} 
        segmentTotals={this.segmentTotals}
        tooltipLabels={this.tooltipLabels}
        segmentRanges={this.segmentRanges}
        colors={this.colors}
        isShowSegmentTotalPercentage={this.isShowSegmentTotalPercentage}>
      </uui-percent-bar-tooltip>
    );
  }
  //#endregion

  showTooltip(event: MouseEvent) {
    if(this.isEnableTooltip) {
      this.displayTooltip = true;
      this.toogleTooltip(true);
      this.updateTooltipPosition(event.x, event.y);
    }
  }

  hideTooltip() {
    if(this.isEnableTooltip) {
      this.toogleTooltip(false);
      this.updateTooltipPosition(-1000, -1000); // to displace the tooltip
    }
  }

  render() {
    const displayValue = this.totalLabel || this.aggregatedTotal;

    return (
      <Host>
        {(!this.segmentTotals || !this.segmentTotals.length) && (<div class="uui-percentile-bar-container uui-dv-legend-label">
          {!this.hasInvalidDataSlot ? (
            <div>Invalid Data</div>
          ) : <slot name="invalid-data" />}
        </div>)}
        {(this.segmentTotals && this.segmentTotals.length > 0) && (<div class="uui-variable-percent-bar-container">
          <div class="uui-variable-percent-bar-quantity uui-dv-legend-label">{displayValue}</div>
          <div class="uui-variable-percent-bar-segments"
            onMouseEnter={(event) => this.showTooltip(event)}
            onMouseLeave={() => this.hideTooltip()}
            onMouseMove={(event) => this.showTooltip(event)}
          >
            {this.segments.filter(s => s.quantity).map(segment => (
              <div
                class="uui-variable-percent-bar-segment"
                style={{
                  backgroundColor: segment.color,
                  width: `${segment.percentOfTotal}%`,
                }}
              ></div>
            ))}
          </div>
        </div>)}

        {/* ------------ Tooltip -------------- */}
        {(this.isEnableTooltip) && (
          <div
            class="uui-variable-percent-bar-tooltip-container uui-variable-percent-bar-tooltip-container-pointer-bottom"
            ref={el => (this.toolTipElement = el)}
          >
          {(this.displayTooltip) && this.getChartTooltip()}
        </div>
        )}
      </Host>
    );
  }
}
